/**
 * StatsCards Component - Key metrics display
 * Shows summary statistics in card format with trends
 */

import { SummaryStats, DistrictStats } from '@/api/ApiService';

export class StatsCards {
  private container: HTMLElement | null = null;

  render(container: HTMLElement): void {
    this.container = container;
    
    container.innerHTML = `
      <div class="hero-section">
        <h2 class="hero-title">Emergency Response Overview</h2>
        <p class="hero-subtitle">Real-time surveillance data from all districts across Sierra Leone</p>
      </div>
      
      <div class="stats-grid" role="region" aria-label="Health statistics">
        <div class="stat-card">
          <div class="card-header">
            <h3 class="card-title">Total Cases</h3>
            <span class="card-icon">📊</span>
          </div>
          <div class="card-number primary" id="total-cases">-</div>
          <div class="card-label">Confirmed cases</div>
          <div class="card-trend" id="cases-trend">
            <span class="trend-text">Loading...</span>
          </div>
        </div>

        <div class="stat-card">
          <div class="card-header">
            <h3 class="card-title">Active Cases</h3>
            <span class="card-icon">🟡</span>
          </div>
          <div class="card-number warning" id="active-cases">-</div>
          <div class="card-label">Currently active</div>
          <div class="card-trend" id="active-trend">
            <span class="trend-text">Loading...</span>
          </div>
        </div>

        <div class="stat-card">
          <div class="card-header">
            <h3 class="card-title">Deaths</h3>
            <span class="card-icon">⚫</span>
          </div>
          <div class="card-number danger" id="deaths">-</div>
          <div class="card-label">Total deaths</div>
          <div class="card-trend" id="deaths-trend">
            <span class="trend-text">Loading...</span>
          </div>
        </div>

        <div class="stat-card">
          <div class="card-header">
            <h3 class="card-title">Vaccinated</h3>
            <span class="card-icon">💚</span>
          </div>
          <div class="card-number success" id="vaccinated">-</div>
          <div class="card-label">People vaccinated</div>
          <div class="card-trend" id="vaccination-trend">
            <span class="trend-text">Loading...</span>
          </div>
        </div>
      </div>
      
      
      <div class="additional-stats" id="additional-stats">
        <div class="stats-row">
          <div class="stat-item">
            <span class="stat-label">Districts Affected:</span>
            <span class="stat-value" id="districts-affected">-</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">New Cases Today:</span>
            <span class="stat-value stat-highlight" id="new-cases-today">-</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Recovery Rate:</span>
            <span class="stat-value" id="recovery-rate">-</span>
          </div>
        </div>
        
        <div class="selected-district" id="selected-district-info" style="display: none;">
          <h3 class="district-title">District Focus: <span id="district-name">-</span></h3>
          <div class="district-stats">
            <div class="district-stat">
              <span class="district-label">Cases:</span>
              <span class="district-value" id="district-cases">-</span>
            </div>
            <div class="district-stat">
              <span class="district-label">Active:</span>
              <span class="district-value" id="district-active">-</span>
            </div>
            <div class="district-stat">
              <span class="district-label">Deaths:</span>
              <span class="district-value" id="district-deaths">-</span>
            </div>
            <div class="district-stat">
              <span class="district-label">Vaccinated:</span>
              <span class="district-value" id="district-vaccinated">-</span>
            </div>
          </div>
        </div>
      </div>
    `;

    this.addStyles();
  }

  async updateData(
    summaryStats: SummaryStats, 
    districtStats?: DistrictStats | null
  ): Promise<void> {
    if (!this.container) return;

    // Update main cards
    this.updateCard('total-cases', summaryStats.total_cases);
    this.updateCard('active-cases', summaryStats.active_cases);
    this.updateCard('deaths', summaryStats.total_deaths);
    this.updateCard('vaccinated', summaryStats.total_vaccinated);

    // Update trends
    this.updateTrend('cases-trend', summaryStats.trend_7_days, summaryStats.new_cases_today);
    this.updateTrend('active-trend', summaryStats.trend_7_days, summaryStats.active_cases);
    this.updateTrend('deaths-trend', summaryStats.trend_7_days, summaryStats.new_deaths_today);
    this.updateTrend('vaccination-trend', 'increasing', 0);

    // Update additional stats
    this.updateAdditionalStats(summaryStats);

    // Update district-specific info
    this.updateDistrictInfo(districtStats);
  }

  private updateCard(elementId: string, value: number): void {
    const element = this.container?.querySelector(`#${elementId}`);
    if (element) {
      element.textContent = this.formatNumber(value);
      
      // Add animation class
      element.classList.add('number-update');
      setTimeout(() => {
        element.classList.remove('number-update');
      }, 300);
    }
  }

  private updateMetaData(stats: SummaryStats): void {
    // Update fatality rate
    const fatalityElement = this.container?.querySelector('#fatality-rate');
    if (fatalityElement) {
      const rate = stats.total_cases > 0 
        ? ((stats.total_deaths / stats.total_cases) * 100).toFixed(1)
        : '0.0';
      fatalityElement.textContent = `${rate}%`;
    }

    // Update vaccine coverage (assuming population of 8 million)
    const coverageElement = this.container?.querySelector('#vaccine-coverage');
    if (coverageElement) {
      const coverage = ((stats.total_vaccinated / 8000000) * 100).toFixed(1);
      coverageElement.textContent = `${coverage}%`;
    }
  }

  private updateTrend(
    elementId: string, 
    trend: 'increasing' | 'decreasing' | 'stable', 
    todayValue: number
  ): void {
    const element = this.container?.querySelector(`#${elementId}`);
    if (!element) return;

    const trendText = element.querySelector('.trend-text');
    
    if (trendText) {
      // Clear existing classes
      element.className = 'card-trend';
      
      switch (trend) {
        case 'increasing':
          element.classList.add('up');
          trendText.textContent = todayValue > 0 ? `+${todayValue} today` : 'Trending up';
          break;
        case 'decreasing':
          element.classList.add('down');
          trendText.textContent = 'Trending down';
          break;
        case 'stable':
          element.classList.add('stable');
          trendText.textContent = todayValue > 0 ? `+${todayValue} today` : 'Stable';
          break;
      }
    }
  }

  private updateAdditionalStats(stats: SummaryStats): void {
    // Districts affected
    const districtsElement = this.container?.querySelector('#districts-affected');
    if (districtsElement) {
      districtsElement.textContent = stats.districts_affected.toString();
    }

    // New cases today
    const newCasesElement = this.container?.querySelector('#new-cases-today');
    if (newCasesElement) {
      newCasesElement.textContent = `+${stats.new_cases_today}`;
      newCasesElement.className = 'stat-value stat-highlight';
      if (stats.new_cases_today > 0) {
        newCasesElement.classList.add('alert-number');
      }
    }

    // Recovery rate
    const recoveryElement = this.container?.querySelector('#recovery-rate');
    if (recoveryElement) {
      const recoveryRate = stats.total_cases > 0 
        ? ((stats.total_recovered / stats.total_cases) * 100).toFixed(1)
        : '0.0';
      recoveryElement.textContent = `${recoveryRate}%`;
    }
  }

  private updateDistrictInfo(districtStats?: DistrictStats | null): void {
    const districtInfo = this.container?.querySelector('#selected-district-info');
    if (!districtInfo) return;

    if (!districtStats) {
      districtInfo.style.display = 'none';
      return;
    }

    districtInfo.style.display = 'block';

    // Update district name
    const nameElement = districtInfo.querySelector('#district-name');
    if (nameElement) {
      nameElement.textContent = districtStats.district_name;
    }

    // Update district stats
    const updates = [
      { id: 'district-cases', value: districtStats.total_cases },
      { id: 'district-active', value: districtStats.active_cases },
      { id: 'district-deaths', value: districtStats.deaths },
      { id: 'district-vaccinated', value: districtStats.vaccinated }
    ];

    updates.forEach(({ id, value }) => {
      const element = districtInfo.querySelector(`#${id}`);
      if (element) {
        element.textContent = this.formatNumber(value);
      }
    });
  }

  showLoading(): void {
    if (!this.container) return;

    // Show skeleton loading for cards
    const cards = this.container.querySelectorAll('.stats-card');
    cards.forEach(card => {
      const numberElement = card.querySelector('.card-number');
      const trendElement = card.querySelector('.card-trend');
      
      if (numberElement) {
        numberElement.innerHTML = '<div class="loading-skeleton skeleton-number"></div>';
      }
      
      if (trendElement) {
        trendElement.innerHTML = '<div class="loading-skeleton skeleton-text"></div>';
      }
    });

    // Show skeleton for additional stats
    const additionalStats = this.container.querySelectorAll('.stat-value');
    additionalStats.forEach(stat => {
      stat.innerHTML = '<div class="loading-skeleton skeleton-text"></div>';
    });
  }

  private formatNumber(num: number): string {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  }

  private addStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      .stats-card {
        position: relative;
        overflow: hidden;
      }
      
      .stats-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(
          90deg, 
          var(--primary-color) 0%, 
          var(--primary-light) 100%
        );
      }
      
      .card-number {
        position: relative;
      }
      
      .number-update {
        animation: numberPulse 0.3s ease-in-out;
      }
      
      @keyframes numberPulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }
      
      .additional-stats {
        background: white;
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow);
        padding: var(--space-6);
        margin-top: var(--space-6);
        margin-bottom: var(--space-8);
      }
      
      .stats-row {
        display: grid;
        grid-template-columns: 1fr;
        gap: var(--space-4);
        margin-bottom: var(--space-6);
      }
      
      @media (min-width: 768px) {
        .stats-row {
          grid-template-columns: repeat(3, 1fr);
        }
      }
      
      .stat-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--space-3);
        background: var(--gray-50);
        border-radius: var(--radius);
      }
      
      .stat-label {
        font-weight: 500;
        color: var(--gray-700);
      }
      
      .stat-value {
        font-weight: 700;
        color: var(--primary-green);
      }
      
      .stat-highlight {
        font-size: var(--text-lg);
      }
      
      .alert-number {
        color: var(--emergency-red);
        animation: alertPulse 2s infinite;
      }
      
      @keyframes alertPulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.7; }
      }
      
      .selected-district {
        border-top: 1px solid var(--gray-200);
        padding-top: var(--space-6);
      }
      
      .district-title {
        font-size: var(--text-lg);
        font-weight: 600;
        color: var(--primary-green);
        margin-bottom: var(--space-4);
        text-align: center;
      }
      
      .district-stats {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--space-3);
      }
      
      @media (min-width: 768px) {
        .district-stats {
          grid-template-columns: repeat(4, 1fr);
        }
      }
      
      .district-stat {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: var(--space-3);
        background: var(--primary-green);
        color: white;
        border-radius: var(--radius);
        text-align: center;
      }
      
      .district-label {
        font-size: var(--text-sm);
        opacity: 0.9;
        margin-bottom: var(--space-1);
      }
      
      .district-value {
        font-size: var(--text-lg);
        font-weight: 700;
      }
      
      /* Loading skeletons */
      .skeleton-number {
        height: 2.5rem;
        width: 80%;
        margin: 0 auto;
      }
      
      .skeleton-text {
        height: 1rem;
        width: 60%;
      }
      
      /* Accessibility improvements */
      @media (prefers-reduced-motion: reduce) {
        .number-update,
        .alert-number {
          animation: none;
        }
      }
      
      /* High contrast mode */
      @media (prefers-contrast: high) {
        .stats-card::before {
          background: var(--primary-color);
        }
        
        .stat-item {
          border: 1px solid var(--gray-400);
        }
      }
    `;
    
    document.head.appendChild(style);
  }
}